import {useQuery} from '@tanstack/react-query';
import ChessRestClient from '@/Api/ChessRestClient';

export type ChapterProgressRosterRow = {
  user_id: number;
  name: string | null;
  role: string;
  /** Most recent attempt. Null when the participant never started. */
  result: 'ongoing' | 'won' | 'lost' | 'drawn' | null;
  /** Best attempt ever, so a later sloppy try doesn't hide a solve. */
  best_result: 'ongoing' | 'won' | 'lost' | 'drawn' | null;
  attempts: number | null;
  move_count: number | null;
  takebacks_used: number | null;
  hint_used: boolean | null;
  completed_at: string | null;
  first_completed_at: string | null;
  updated_at: string | null;
};

export type ChapterProgressRoster = {
  chapter_id: number;
  chapter_mode: string;
  participant_count: number;
  completed_count: number;
  attempted_count: number;
  rows: ChapterProgressRosterRow[];
};

/**
 * Every participant's result on one chapter — the coach's read side of an
 * Exercise. Owner-only on the backend, so `enabled` should mirror that rather
 * than firing a guaranteed 403 for participants.
 */
export default function useGetChapterProgressRoster(
  chapterId: number | string | undefined,
  enabled: boolean,
) {
  return useQuery<ChapterProgressRoster | null>(
    ['chapter-progress-roster', chapterId],
    async () => {
      const response = await ChessRestClient.get(
        `/learning/study-chapter/${chapterId}/progress/roster/`,
      );
      return response.data?.data ?? null;
    },
    {
      enabled: Boolean(chapterId) && enabled,
      staleTime: 30_000,
    },
  );
}
